/* import __COLOCATED_TEMPLATE__ from './compensation-config.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import ajax from 'embercom/lib/ajax';
import type ShopifyIntegration from 'embercom/models/shopify/integration';

type CompensationField =
  | 'partialRefundsEnabled'
  | 'replacementOrdersEnabled'
  | 'giftCardCompensationEnabled'
  | 'replacementItemsEnabled'
  | 'perItemRefundReasonEnabled';

interface CompensationConfigSignature {
  Args: {
    shops: ShopifyIntegration[];
  };
}

export default class CompensationConfig extends Component<CompensationConfigSignature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;

  @tracked private pendingPartialRefundsEnabled: boolean | null = null;
  @tracked private pendingReplacementOrdersEnabled: boolean | null = null;
  @tracked private pendingGiftCardCompensationEnabled: boolean | null = null;
  @tracked private pendingReplacementItemsEnabled: boolean | null = null;
  @tracked private pendingPerItemRefundReasonEnabled: boolean | null = null;

  get shop(): ShopifyIntegration | undefined {
    return this.args.shops[0];
  }

  get partialRefundsEnabled(): boolean {
    return this.pendingPartialRefundsEnabled ?? this.shop?.partialRefundsEnabled ?? false;
  }

  set partialRefundsEnabled(value: boolean) {
    this.pendingPartialRefundsEnabled = value;
  }

  get replacementOrdersEnabled(): boolean {
    return this.pendingReplacementOrdersEnabled ?? this.shop?.replacementOrdersEnabled ?? false;
  }

  set replacementOrdersEnabled(value: boolean) {
    this.pendingReplacementOrdersEnabled = value;
  }

  get giftCardCompensationEnabled(): boolean {
    return (
      this.pendingGiftCardCompensationEnabled ?? this.shop?.giftCardCompensationEnabled ?? false
    );
  }

  set giftCardCompensationEnabled(value: boolean) {
    this.pendingGiftCardCompensationEnabled = value;
  }

  get replacementItemsEnabled(): boolean {
    return this.pendingReplacementItemsEnabled ?? this.shop?.replacementItemsEnabled ?? false;
  }

  set replacementItemsEnabled(value: boolean) {
    this.pendingReplacementItemsEnabled = value;
  }

  get perItemRefundReasonEnabled(): boolean {
    return this.pendingPerItemRefundReasonEnabled ?? this.shop?.perItemRefundReasonEnabled ?? false;
  }

  set perItemRefundReasonEnabled(value: boolean) {
    this.pendingPerItemRefundReasonEnabled = value;
  }

  get compensationFields(): { field: CompensationField; label: string }[] {
    return [
      {
        field: 'partialRefundsEnabled',
        label: this.intl.t('appstore.settings.shopify.compensation-config.partial-refunds'),
      },
      {
        field: 'replacementOrdersEnabled',
        label: this.intl.t('appstore.settings.shopify.compensation-config.replacement-orders'),
      },
      {
        field: 'giftCardCompensationEnabled',
        label: this.intl.t('appstore.settings.shopify.compensation-config.gift-card-compensation'),
      },
      {
        field: 'replacementItemsEnabled',
        label: this.intl.t('appstore.settings.shopify.compensation-config.replacement-items'),
      },
      {
        field: 'perItemRefundReasonEnabled',
        label: this.intl.t('appstore.settings.shopify.compensation-config.per-item-refund-reason'),
      },
    ];
  }

  @action
  toggleField(field: CompensationField) {
    let newValue = !this[field];
    this[field] = newValue;
    void taskFor(this.updateConfig).perform(field, newValue);
  }

  private fieldToParam: Record<CompensationField, string> = {
    partialRefundsEnabled: 'partial_refunds_enabled',
    replacementOrdersEnabled: 'replacement_orders_enabled',
    giftCardCompensationEnabled: 'gift_card_compensation_enabled',
    replacementItemsEnabled: 'replacement_items_enabled',
    perItemRefundReasonEnabled: 'per_item_refund_reason_enabled',
  };

  @enqueueTask
  *updateConfig(field: CompensationField, value: boolean): TaskGenerator<void> {
    try {
      yield ajax({
        type: 'PUT',
        url: `/ember/shopify/integrations/update_compensation_config?app_id=${this.appService.app.id}`,
        data: JSON.stringify({
          [this.fieldToParam[field]]: value,
        }),
      });

      if (this.shop) {
        this.shop[field] = value;
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('appstore.settings.shopify.compensation-config.save-success'),
      );
    } catch (error: unknown) {
      this[field] = !value;
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('appstore.settings.shopify.compensation-config.save-failure'),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Appstore::AppPackage::Settings::Shopify::CompensationConfig': typeof CompensationConfig;
    'appstore/app-package/settings/shopify/compensation-config': typeof CompensationConfig;
  }
}
